/**
 * Generic quotation step shared by all configurators: summary, contact form,
 * mockup + artwork upload, submit, and the resulting job ticket.
 */
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { RFQ_SCHEMA_VERSION, validateRfq, type Configuration, type RfqPayload } from '@shared/rfq';
import { configurationLines, rfqToPlainText, type LineId } from '@shared/rfqText';
import { fmt, useI18n } from '@/i18n';
import { isApiAvailable, submitRfq, uploadFile } from '@/lib/api';
import { formatBytes } from '../artwork/ArtworkUploader';
import type { ProcessedArtwork } from '../artwork/processArtwork';

export interface RfqStepProps {
  quantity: number;
  skuCount: number;
  targetDate: string;
  /** Label for the quantity input, e.g. "Кількість наборів". */
  quantityLabel?: string;
  artwork: ProcessedArtwork | null;
  uploadedArtworkId: string | null;
  buildConfiguration: (ids: { artworkId: string | null; mockupId: string | null }) => Configuration;
  exportMockup: () => Promise<Blob | null>;
  onArtworkUploaded: (id: string) => void;
  onQuantity: (q: number) => void;
  onEdit?: (line: LineId) => void;
  editable?: LineId[];
  onReset: () => void;
}

interface ContactForm {
  company: string;
  contactPerson: string;
  email: string;
  phone: string;
  comments: string;
  consent: boolean;
  website: string;
}

const EMPTY: ContactForm = { company: '', contactPerson: '', email: '', phone: '', comments: '', consent: false, website: '' };

type Result = { mode: 'server'; rfqNumber: string } | { mode: 'offline' };

export function RfqStep(p: RfqStepProps) {
  const { d, locale } = useI18n();
  const t = d.cfg.rfq;
  const [form, setForm] = useState<ContactForm>(EMPTY);
  const [qty, setQty] = useState(String(p.quantity));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [result, setResult] = useState<Result | null>(null);
  const [payload, setPayload] = useState<RfqPayload | null>(null);
  const [mockupUrl, setMockupUrl] = useState<string | null>(null);

  useEffect(() => setQty(String(p.quantity)), [p.quantity]);
  useEffect(() => () => void (mockupUrl && URL.revokeObjectURL(mockupUrl)), [mockupUrl]);

  const buildPayload = (config: Configuration): RfqPayload => ({
    schemaVersion: RFQ_SCHEMA_VERSION,
    kind: 'configurator',
    locale,
    contact: { company: form.company, contactPerson: form.contactPerson, email: form.email, phone: form.phone },
    order: { quantity: Math.round(Number(qty)), skuCount: p.skuCount, targetDate: p.targetDate || null },
    configuration: config,
    productInterest: null,
    comments: form.comments,
    consent: form.consent,
    website: form.website,
    clientCreatedAt: new Date().toISOString(),
  });

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setServerError(null);
    const draft = buildPayload(p.buildConfiguration({ artworkId: p.uploadedArtworkId, mockupId: null }));
    const v = validateRfq(draft);
    if (!v.ok) {
      setErrors(v.fields);
      const first = Object.keys(v.fields)[0];
      document.getElementById(`rfq-${first}`)?.focus();
      return;
    }
    setErrors({});
    setBusy(true);
    const qn = Math.round(Number(qty));
    if (qn !== p.quantity) p.onQuantity(qn);

    // Mockup of the current front view, attached to the RFQ.
    const mockBlob = await p.exportMockup();
    if (mockBlob) setMockupUrl(URL.createObjectURL(mockBlob));

    const online = await isApiAvailable();
    if (!online) {
      setPayload(v.value);
      setResult({ mode: 'offline' });
      setBusy(false);
      return;
    }

    let artworkId = p.uploadedArtworkId;
    if (p.artwork && !artworkId) {
      try {
        artworkId = (await uploadFile(p.artwork.uploadBlob, p.artwork.fileName, 'artwork')).id;
        p.onArtworkUploaded(artworkId);
      } catch {
        /* RFQ still goes out; staff will ask for the file */
      }
    }
    let mockupId: string | null = null;
    if (mockBlob) {
      try {
        mockupId = (await uploadFile(mockBlob, `mockup-${Date.now()}.png`, 'mockup')).id;
      } catch {
        /* optional */
      }
    }
    const config = p.buildConfiguration({ artworkId, mockupId });
    const final = { ...v.value, configuration: config };
    const res = await submitRfq(final);
    setBusy(false);
    if (res.ok) {
      setPayload(final);
      setResult({ mode: 'server', rfqNumber: res.rfqNumber });
    } else if (res.error === 'validation_failed' && res.fields) {
      setErrors(res.fields);
    } else {
      setServerError(res.error === 'network' ? d.errors.network : d.errors.server);
    }
  };

  if (result && payload) {
    return <RfqDone result={result} payload={payload} mockupUrl={mockupUrl} onNew={p.onReset} />;
  }

  const field = (key: keyof ContactForm, label: string, type = 'text', autoComplete?: string) => (
    <div className={`field${errors[key] ? ' has-error' : ''}`}>
      <label htmlFor={`rfq-${key}`}>{label}</label>
      <input
        id={`rfq-${key}`}
        type={type}
        autoComplete={autoComplete}
        value={form[key] as string}
        aria-invalid={!!errors[key]}
        aria-describedby={errors[key] ? `rfq-${key}-err` : undefined}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
      />
      {errors[key] && (
        <span id={`rfq-${key}-err`} className="field__error">
          {d.validation[errors[key].replace('validation.', '') as keyof typeof d.validation] ?? errors[key]}
        </span>
      )}
    </div>
  );

  const config = p.buildConfiguration({ artworkId: p.uploadedArtworkId, mockupId: null });

  return (
    <div className="step">
      <h2 className="step__title">{t.title}</h2>
      <p className="step__lead">{t.lead}</p>

      <section className="ticket" aria-label={t.summary}>
        <header className="ticket__head">
          <span className="mono">{t.summary}</span>
        </header>
        <ConfigTable config={config} onEdit={p.onEdit} editable={p.editable} />
      </section>

      <form className="form" onSubmit={onSubmit} noValidate>
        <div className="form__grid">
          {field('company', d.form.company, 'text', 'organization')}
          {field('contactPerson', d.form.contactPerson, 'text', 'name')}
          {field('email', d.form.email, 'email', 'email')}
          {field('phone', d.form.phone, 'tel', 'tel')}
          <div className={`field${errors.quantity ? ' has-error' : ''}`}>
            <label htmlFor="rfq-quantity">{p.quantityLabel ?? d.form.quantity}</label>
            <input id="rfq-quantity" type="number" min={1} inputMode="numeric" value={qty} onChange={(e) => setQty(e.target.value)} />
            {errors.quantity && <span className="field__error">{d.validation.quantity}</span>}
          </div>
        </div>
        <div className="field">
          <label htmlFor="rfq-comments">{d.form.comments}</label>
          <textarea id="rfq-comments" rows={4} placeholder={d.form.commentsPh} value={form.comments} onChange={(e) => setForm({ ...form, comments: e.target.value })} />
        </div>
        <div className="hp" aria-hidden="true">
          <label htmlFor="rfq-website">Website</label>
          <input id="rfq-website" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
        </div>
        <label className={`check${errors.consent ? ' has-error' : ''}`}>
          <input id="rfq-consent" type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} />
          <span>{d.form.consent}</span>
        </label>
        {errors.consent && <span className="field__error">{d.validation.consent}</span>}
        {serverError && (
          <p className="notice notice--error" role="alert">
            {serverError}
          </p>
        )}
        <button type="submit" className="btn btn--primary btn--lg" disabled={busy}>
          {busy ? t.submitting : t.submit}
        </button>
      </form>
    </div>
  );
}

export function ConfigTable({ config, onEdit, editable = [] }: { config: Configuration; onEdit?: (line: LineId) => void; editable?: LineId[] }) {
  const { d } = useI18n();
  const lines = configurationLines(config);
  return (
    <dl className="ticket__rows">
      {lines.map(([id, k, v]) => (
        <div key={id} className="ticket__row">
          <dt>{k}</dt>
          <dd>
            <span>{v}</span>
            {onEdit && editable.includes(id) && (
              <button type="button" className="link-btn" onClick={() => onEdit(id)}>
                {d.cfg.rfq.edit}
              </button>
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function RfqDone({ result, payload, mockupUrl, onNew }: { result: Result; payload: RfqPayload; mockupUrl: string | null; onNew: () => void }) {
  const { d, nf } = useI18n();
  const t = d.cfg.rfq;
  const [copied, setCopied] = useState(false);
  const preRef = useRef<HTMLPreElement>(null);
  const rfqNumber = result.mode === 'server' ? result.rfqNumber : undefined;
  const text = useMemo(() => rfqToPlainText({ ...payload, rfqNumber }), [payload, rfqNumber]);
  const jsonUrl = useMemo(
    () => URL.createObjectURL(new Blob([JSON.stringify({ ...payload, rfqNumber: rfqNumber ?? null }, null, 2)], { type: 'application/json' })),
    [payload, rfqNumber],
  );
  useEffect(() => () => URL.revokeObjectURL(jsonUrl), [jsonUrl]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      const sel = window.getSelection();
      if (preRef.current && sel) {
        const range = document.createRange();
        range.selectNodeContents(preRef.current);
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }
  };

  const cfg = payload.configuration!;
  const art = cfg.product === 'gift' ? cfg.logo : cfg.artwork;
  const unit = cfg.product === 'gift' ? d.gift.sets : d.common.pcs;
  return (
    <div className="step step--done">
      {result.mode === 'server' ? (
        <>
          <p className="done__badge mono">{result.rfqNumber}</p>
          <h2 className="step__title">{fmt(t.successTitle, { n: result.rfqNumber })}</h2>
          <p className="step__lead">{fmt(t.successText, { email: payload.contact.email })}</p>
        </>
      ) : (
        <>
          <h2 className="step__title">{t.draftTitle}</h2>
          <p className="notice notice--warn">{d.errors.offline}</p>
        </>
      )}

      <section className="ticket">
        <header className="ticket__head">
          <span className="mono">{rfqNumber ?? 'DRAFT'}</span>
          <span className="mono">{new Date().toLocaleDateString('uk-UA')}</span>
        </header>
        {mockupUrl && (
          <figure className="ticket__mock checker">
            <img src={mockupUrl} alt={t.mockup} />
          </figure>
        )}
        <dl className="ticket__rows">
          <div className="ticket__row">
            <dt>{d.form.company}</dt>
            <dd>{payload.contact.company}</dd>
          </div>
          <div className="ticket__row">
            <dt>{d.form.contactPerson}</dt>
            <dd>
              {payload.contact.contactPerson} · {payload.contact.email} · {payload.contact.phone}
            </dd>
          </div>
          <div className="ticket__row">
            <dt>{d.cfg.summary.quantity}</dt>
            <dd>
              {nf(payload.order!.quantity)} {unit} · {d.cfg.summary.skus}: {payload.order!.skuCount}
              {payload.order!.targetDate ? ` · ${payload.order!.targetDate}` : ''}
            </dd>
          </div>
        </dl>
        <ConfigTable config={cfg} />
        {art && <p className="ticket__foot mono">{formatBytes(art.sizeBytes)} · {art.sha256.slice(0, 24)}…</p>}
      </section>

      <pre ref={preRef} className="ticket__text" aria-label={t.summary}>
        {text}
      </pre>

      <div className="btn-row">
        <button type="button" className="btn btn--ghost" onClick={copy}>
          {copied ? d.common.copied : t.copySummary}
        </button>
        <a className="btn btn--ghost" href={jsonUrl} download={`${rfqNumber ?? 'rfq-draft'}.json`}>
          {t.downloadJson}
        </a>
        <button type="button" className="btn btn--primary" onClick={onNew}>
          {t.newConfig}
        </button>
      </div>
    </div>
  );
}
